import type { TrackingSpecs } from '@/libraries/tracking/specTracker';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { PosterTitle } from '@/interfaces/components/poster/PosterGraphql';
import type { MaybeRefOrGetter } from '@vueuse/core';
import type { Maybe } from '@/helpers/types';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { createSpecTracker, defineSpec } from '@/libraries/tracking/specTracker';
import { toValue } from '@vueuse/core';
import { computed, ref } from 'vue';

const specs = {
	/** Click on Add why to watch or "Next" button in empty WtW state. */
	modalOpened: { action: 'modal_opened' },

	modalClosed: { action: 'modal_closed' },

	/** Clicking the "next" button on the empty text recommendation form. */
	emptyNextClick: { action: 'modal_opened' },

	/** Click on Who do you recommend... */
	headlineFocused: { action: 'field_clicked', label: 'who' },

	/** "Who do you recommend..." field filled in then click outside. */
	headlineBlurred: {
		action: 'field_completed',
		label: 'who',
		triggerCheck: (headline: string) => headline.trim().length > 10,
	},

	/** Click on "Watch if you..." field */
	watchIfFocused: { action: 'field_clicked', label: 'watch_if' },

	/** "Watch if you..." field filled in then click outside. */
	watchIfBlurred: {
		action: 'field_completed',
		label: 'watch_if',
		triggerCheck: (watchIf: string) => watchIf.trim().length > 30,
	},

	/** Click on "Skip if you..." field */
	skipIfFocused: { action: 'field_clicked', label: 'skip_if' },

	/** "Skip if you..." field filled in then click outside. */
	skipIfBlurred: {
		action: 'field_completed',
		label: 'skip_if',
		triggerCheck: (skipIf: string) => skipIf.trim().length > 30,
	},

	/** Click on Why should someone... */
	bodyFocused: { action: 'field_clicked', label: 'why' },

	/** "Who should someone..." field filled in then click outside. */
	bodyBlurred: {
		action: 'field_completed',
		label: 'why',
		triggerCheck: (body: string) => body.trim().length > 60,
	},

	/** When the provider selector is clicked. */
	providerDropdownClicked: {
		action: 'dropdown_clicked',
		label: 'provider',
	},

	providerSelected: {
		action: 'provider_selected',
		label: 'provider',
	},

	nameFocused: {
		action: 'field_clicked',
		label: 'name',
	},

	nameBlurred: {
		action: 'field_completed',
		label: 'name',
		triggerCheck: (name: string) => name.trim().length > 0,
	},

	uploadImageClick: {
		action: 'upload_image_clicked',
		label: 'image',
	},

	imageSelected: {
		action: 'image_selected',
		label: 'image',
	},

	/** Submit the why to watch. */
	addWhyToWatch: {
		action: 'wtw_completed',
	},

	/** How many times the disabled button gets clicked. */
	disabledAddWhyToWatchClicked: defineSpec<{
		action: `wtw_completed_fail_${number}`;
	}>()(),
} satisfies TrackingSpecs;

export function useWtwTracking(options: { title: MaybeRefOrGetter<Maybe<TitleDetail | PosterTitle>> }) {
	const titleContext = computed(() => {
		const title = toValue(options.title);
		if (title == null) return null;

		const objectId = title.__typename === 'Season' ? title.show.objectId : title.objectId;
		const objectType = title.__typename === 'Season' ? title.show.objectType : title.objectType;
		const seasonNumber = title.__typename === 'Season' ? title.content?.seasonNumber : undefined;

		return new SnowplowTitleContextGraphql(objectId, objectType, seasonNumber);
	});

	const trackWtw = createSpecTracker(specs, { property: 'wtw_recommendation' }, () => [titleContext.value]);

	const disableClickCount = ref(0);

	function trackWtwDisabledClick() {
		trackWtw('disabledAddWhyToWatchClicked', {
			spec: { action: `wtw_completed_fail_${disableClickCount.value++}` },
		});
	}

	return { trackWtw, trackWtwDisabledClick };
}
